import type {
  CandleInterval,
  ChartAlertItem,
  ChartDrawingItem,
  OverlaySelection,
  OverlaySettings,
  PaperTradingAccountSummary,
  PaperTradingFill,
  PaperTradingOrderItem,
  PaperTradingPosition,
} from '../types';
import type { MarketStatus, ProviderName, SessionState } from '../lib/providerRouting';
import type { AiProviderId } from './ai';

export type UserRole = 'owner' | 'admin' | 'user';

export interface AuthUser {
  id: number;
  username: string;
  role: UserRole;
}

export interface UserPreferencesPayload {
  timeframe: CandleInterval;
  overlays: OverlaySelection;
  overlaySettings: OverlaySettings;
  indicatorLabelsVisible?: boolean;
  showMarketStatus?: boolean;
  showIndicatorTitles?: boolean;
  mobileDrawingToolbarCollapsed?: boolean;
  alerts: ChartAlertItem[];
  drawings: ChartDrawingItem[];
  aiChatLastModel?: AiChatLastModelPreference | null;
}

export interface AiChatLastModelPreference {
  provider: AiProviderId;
  modelId: string;
}

export interface AccessGrant {
  email: string;
  addedAt: string | null;
  addedByUserId: number | null;
  source: 'env' | 'db';
}

export interface AccessOverview {
  currentRole: UserRole;
  owners: AccessGrant[];
  admins: AccessGrant[];
  users: AccessGrant[];
}

export interface ProviderKeyStatus {
  provider: string;
  displayName: string;
  keySuffix: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderKeysOverview {
  storageReady: boolean;
  providers: ProviderKeyStatus[];
}

export interface AdminDiagnosticsCacheSummary {
  path: string;
  totalCandleCount: number;
  providersWithCachedCandles: number;
  latestPopularityUpdateAt: string | null;
  totalSymbolCatalogCount: number;
  latestSymbolCatalogUpdateAt: string | null;
  symbolCatalogWarming: boolean;
}

export interface AdminDiagnosticsCatalogRefresh {
  status: string;
  lastSuccessfulRefreshAt: string | null;
  lastAttemptedRefreshAt: string | null;
  nextRetryAt: string | null;
  lastError: string;
}

export interface AdminDiagnosticsStreamStatus {
  mode: 'stream' | 'rest';
  configured: boolean;
  connected: boolean;
  streamCount: number;
  subscriberCount: number;
  latestTickAt: string | null;
  latestOpenInterest: number | null;
}

export interface AdminDiagnosticsRateLimit {
  isBlocked: boolean;
  remaining: number | null;
  resetAt: string | null;
  blockedUntilAt: string | null;
}

export interface AdminDiagnosticsTransportStatus {
  kind: 'websocket' | 'http_stream';
  connected: boolean;
  checkedAt: string;
  latencyMs: number | null;
  endpoint: string;
  target: string | null;
  lastError: string;
}

export interface AdminDiagnosticsProvider {
  provider: ProviderName;
  displayName: string;
  configured: boolean;
  connectionKind: 'stream' | 'rest';
  marketStatus: MarketStatus;
  sessionState: SessionState;
  candleCacheCount: number;
  cachedSymbolCount: number;
  cachedIntervalCount: number;
  latestCandleAt: string | null;
  symbolCatalogCount: number;
  catalogRefresh: AdminDiagnosticsCatalogRefresh;
  stream: AdminDiagnosticsStreamStatus;
  transport: AdminDiagnosticsTransportStatus | null;
  rateLimit: AdminDiagnosticsRateLimit | null;
}

export interface AdminDiagnosticsOverview {
  generatedAt: string;
  cache: AdminDiagnosticsCacheSummary;
  providers: AdminDiagnosticsProvider[];
}

export interface PaperTradingStateResponse {
  account: PaperTradingAccountSummary;
  orders: PaperTradingOrderItem[];
  positions: PaperTradingPosition[];
}

export interface CreatePaperTradingOrderPayload {
  provider: ProviderName;
  symbol: string;
  side: 'buy' | 'sell';
  orderType: 'limit' | 'stop' | 'market';
  price: number;
  quantity: number;
}

interface SavePreferencesOptions {
  keepalive?: boolean;
}

interface AuthResponse {
  user: AuthUser;
}

interface ApiErrorPayload {
  detail?: string;
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function parseApiError(response: Response): Promise<ApiRequestError> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const payload = (await response.json()) as ApiErrorPayload;
    if (typeof payload.detail === 'string' && payload.detail.length > 0) {
      return new ApiRequestError(payload.detail, response.status);
    }
  }
  return new ApiRequestError('Request failed with status ' + response.status, response.status);
}

async function requestJson<T>(input: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return (await response.json()) as T;
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  const response = await fetch('/api/auth/me', {
    credentials: 'include',
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return (await response.json()) as AuthUser;
}

export async function googleLogin(credential: string): Promise<AuthUser> {
  const response = await requestJson<AuthResponse>('/api/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  });
  return response.user;
}

export async function getAccessOverview(): Promise<AccessOverview> {
  return requestJson<AccessOverview>('/api/auth/access', {
    method: 'GET',
  });
}

export async function grantAdminAccess(email: string): Promise<AccessGrant> {
  return requestJson<AccessGrant>('/api/auth/access/admins', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function revokeAdminAccess(email: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/auth/access/admins/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

export async function grantUserAccess(email: string): Promise<AccessGrant> {
  return requestJson<AccessGrant>('/api/auth/access/users', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function revokeUserAccess(email: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/auth/access/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

export async function logout(): Promise<void> {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw await parseApiError(response);
  }
}

export async function getPreferences(): Promise<UserPreferencesPayload> {
  return requestJson<UserPreferencesPayload>('/api/auth/preferences', {
    method: 'GET',
  });
}

export async function savePreferences(
  payload: UserPreferencesPayload,
  options: SavePreferencesOptions = {},
): Promise<void> {
  await requestJson<UserPreferencesPayload>('/api/auth/preferences', {
    method: 'PUT',
    keepalive: options.keepalive,
    body: JSON.stringify(payload),
  });
}

export async function getProviderKeys(): Promise<ProviderKeysOverview> {
  return requestJson<ProviderKeysOverview>('/api/auth/provider-keys', {
    method: 'GET',
  });
}

export async function getAdminDiagnostics(): Promise<AdminDiagnosticsOverview> {
  return requestJson<AdminDiagnosticsOverview>('/api/auth/diagnostics', {
    method: 'GET',
  });
}

export async function saveProviderKey(provider: string, apiKey: string): Promise<ProviderKeyStatus> {
  return requestJson<ProviderKeyStatus>(`/api/auth/provider-keys/${encodeURIComponent(provider)}`, {
    method: 'PUT',
    body: JSON.stringify({ apiKey }),
  });
}

export async function deleteProviderKey(provider: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/auth/provider-keys/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
  });
}

export async function getPaperTradingState(
  provider?: ProviderName,
  symbol?: string,
): Promise<PaperTradingStateResponse> {
  const search = new URLSearchParams();
  if ((provider === undefined) !== (symbol === undefined)) {
    throw new Error('Provider and symbol must be provided together.');
  }
  if (provider && symbol && symbol.trim().length > 0) {
    search.set('provider', provider);
    search.set('symbol', symbol.trim().toUpperCase());
  }

  const suffix = search.size > 0 ? `?${search.toString()}` : '';
  return requestJson<PaperTradingStateResponse>(`/api/paper-trading/state${suffix}`, {
    method: 'GET',
  });
}

export async function getPaperTradingFills(
  provider?: ProviderName,
  symbol?: string,
): Promise<PaperTradingFill[]> {
  const search = new URLSearchParams();
  if ((provider === undefined) !== (symbol === undefined)) {
    throw new Error('Provider and symbol must be provided together.');
  }
  if (provider && symbol && symbol.trim().length > 0) {
    search.set('provider', provider);
    search.set('symbol', symbol.trim().toUpperCase());
  }

  const suffix = search.size > 0 ? `?${search.toString()}` : '';
  return requestJson<PaperTradingFill[]>(`/api/paper-trading/fills${suffix}`, {
    method: 'GET',
  });
}

export async function createPaperTradingOrder(
  payload: CreatePaperTradingOrderPayload,
): Promise<PaperTradingOrderItem> {
  return requestJson<PaperTradingOrderItem>('/api/paper-trading/orders', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function cancelPaperTradingOrder(orderId: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/paper-trading/orders/${encodeURIComponent(orderId)}`, {
    method: 'DELETE',
  });
}

export async function cancelPaperTradingOrdersForSymbol(provider: ProviderName, symbol: string): Promise<void> {
  const search = new URLSearchParams({ provider, symbol: symbol.trim().toUpperCase() });
  await requestJson<{ ok: true }>(`/api/paper-trading/orders?${search.toString()}`, {
    method: 'DELETE',
  });
}

export async function resetPaperTradingAccount(): Promise<void> {
  await requestJson<{ ok: true }>('/api/paper-trading/reset', {
    method: 'DELETE',
  });
}
